import React from 'react'
import { Link, useLocation } from 'react-router-dom'
import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { useOptimizedScroll } from '../hooks/useOptimizedScroll'
import { useDivisionRouting } from '../hooks/useDivisionRouting'
import { useIsMobile } from '../hooks/useMediaQuery'
import { MobileNav } from './MobileNav'

export const GlobalNavbar: React.FC = () => {
  const { t } = useTranslation()
  const location = useLocation()
  const { isScrolled } = useOptimizedScroll()
  const isMobile = useIsMobile()
  
  // Division routing hook for services navigation
  const { navigateToDivision } = useDivisionRouting({
    onDivisionChange: () => {},
    onProductRoute: () => {},
  })

  // Render mobile nav on small screens
  if (isMobile) {
    return <MobileNav />
  }

  return (
    <div className="fixed top-0 left-0 right-0 z-50 flex justify-center pointer-events-none">
      <motion.nav 
        className="backdrop-blur-xl rounded-full shadow-lg transition-all duration-500 pointer-events-auto"
        style={{
          backgroundColor: isScrolled ? 'rgba(247, 245, 245, 0.95)' : 'rgba(247, 245, 245, 0.7)',
          border: '1px solid rgba(0, 0, 0, 0.1)',
          marginTop: isScrolled ? '20px' : '30px',
          padding: isScrolled ? '12px 24px' : '16px 32px'
        }}
        initial={{ y: -120, opacity: 0, scale: 0.95 }}
        animate={{ y: 0, opacity: 1, scale: 1 }}
        transition={{ 
          duration: 0.4, 
          ease: [0.25, 0.46, 0.45, 0.94], // Apple's easing
          delay: 0 // No delay for instant appearance
        }}
      >
        <div className="flex items-center gap-8">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 no-underline">
            <img
              src="/Mernaki_Logo_Pack/transparent/mernaki-128.png"
              alt="Mernaki Logo"
              className="object-contain"
              style={{ height: '38px', width: 'auto' }}
            />
            <h1 
              className="text-xl"
              style={{ 
                color: '#1A1A1A',
                fontFamily: "'Brush Script MT', 'Lucida Handwriting', 'Apple Chancery', cursive",
                fontWeight: 400
              }}
            >
              Mernaki
            </h1>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-6">
            <Link
              to="/products"
              className="text-sm transition-all duration-300"
              style={{ 
                color: location.pathname === '/products' ? '#1A1A1A' : '#666666',
                fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                fontWeight: 600,
                textDecoration: 'none'
              }}
              onMouseEnter={(e) => e.currentTarget.style.color = '#1A1A1A'}
              onMouseLeave={(e) => e.currentTarget.style.color = location.pathname === '/products' ? '#1A1A1A' : '#666666'}
            >
              {t('nav.products', 'Products')}
            </Link>

            <button
              onClick={() => navigateToDivision('services')}
              className="text-sm transition-all duration-300"
              style={{ 
                color: location.pathname === '/services' ? '#1A1A1A' : '#666666',
                fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                fontWeight: 600,
                background: 'none',
                border: 'none',
                cursor: 'pointer'
              }}
              onMouseEnter={(e) => e.currentTarget.style.color = '#1A1A1A'}
              onMouseLeave={(e) => e.currentTarget.style.color = location.pathname === '/services' ? '#1A1A1A' : '#666666'}
            >
              {t('nav.services', 'Services')}
            </button>

            <Link
              to="/booking"
              className="text-sm transition-all duration-300"
              style={{ 
                color: location.pathname === '/booking' || location.pathname === '/book' ? '#1A1A1A' : '#666666',
                fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                fontWeight: 600,
                textDecoration: 'none'
              }}
              onMouseEnter={(e) => e.currentTarget.style.color = '#1A1A1A'}
              onMouseLeave={(e) => e.currentTarget.style.color = (location.pathname === '/booking' || location.pathname === '/book') ? '#1A1A1A' : '#666666'}
            >
              {t('nav.booking', 'Booking')}
            </Link>

            <Link
              to="/about"
              className="text-sm transition-all duration-300"
              style={{ 
                color: location.pathname === '/about' ? '#1A1A1A' : '#666666',
                fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                fontWeight: 600,
                textDecoration: 'none'
              }}
              onMouseEnter={(e) => e.currentTarget.style.color = '#1A1A1A'}
              onMouseLeave={(e) => e.currentTarget.style.color = location.pathname === '/about' ? '#1A1A1A' : '#666666'}
            >
              {t('nav.about', 'About')}
            </Link>

            <Link
              to="/contact"
              className="text-sm transition-all duration-300"
              style={{ 
                color: location.pathname === '/contact' ? '#1A1A1A' : '#666666',
                fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                fontWeight: 600,
                textDecoration: 'none'
              }}
              onMouseEnter={(e) => e.currentTarget.style.color = '#1A1A1A'}
              onMouseLeave={(e) => e.currentTarget.style.color = location.pathname === '/contact' ? '#1A1A1A' : '#666666'}
            >
              {t('nav.contact', 'Contact')}
            </Link>
          </div>
        </div>
      </motion.nav>
    </div>
  )
}